import { $, $$, setLoading } from '../lib/dom.js';
import { api } from '../lib/api.js';
import { toastError } from '../lib/toast.js';
import { ct } from '../lib/i18n.js';

/**
 * Redirection to Stripe.
 *
 * The old premium page displayed a home-made form asking for name, e-mail, card
 * number, expiry date and CVV, then sent them to an internal route. Here, the
 * application never sees any banking data: it asks its own server for a session
 * URL and lets Stripe take over.
 */
export function initBillingPage(): void {
  watchActivation();

  for (const button of $$<HTMLButtonElement>('[data-checkout]')) {
    button.addEventListener('click', () => {
      void (async () => {
        const plan = button.dataset.checkout;
        const guildId = button.dataset.guildId ?? '';

        setLoading(button, true);
        try {
          const result = await api.post<{ url: string }>(
            `/api/billing/checkout?guildId=${guildId}`,
            { guildId, plan },
          );
          window.location.assign(result.url);
        } catch (error) {
          toastError(error);
          setLoading(button, false);
        }
      })();
    });
  }

  for (const button of $$<HTMLButtonElement>('[data-billing-portal]')) {
    button.addEventListener('click', () => {
      void (async () => {
        const guildId = button.dataset.guildId ?? '';

        setLoading(button, true);
        try {
          const result = await api.post<{ url: string }>(`/api/billing/portal?guildId=${guildId}`);
          window.location.assign(result.url);
        } catch (error) {
          toastError(error);
          setLoading(button, false);
        }
      })();
    });
  }

  // The buyer's own portal, from the account page: no server guard to pass.
  for (const button of $$<HTMLButtonElement>('[data-owned-portal]')) {
    button.addEventListener('click', () => {
      void (async () => {
        setLoading(button, true);
        try {
          const result = await api.post<{ url: string }>('/api/billing/owned-portal', {
            guildId: button.dataset.guildId ?? '',
          });
          window.location.assign(result.url);
        } catch (error) {
          toastError(error);
          setLoading(button, false);
        }
      })();
    });
  }

  void offerWithdrawal();
  watchForStaleState();
}

/**
 * Reloads when the subscription has moved on without telling the page.
 *
 * A visitor who cancels in the Stripe portal comes back to a tab rendered
 * before the cancellation: "renews on 29 September" over a subscription that
 * has just been ended. The page has no way to know — nothing pushes it.
 *
 * Read on regaining focus rather than polled: the only thing that changes a
 * subscription behind this page's back is the visitor going somewhere else to
 * change it, and they come back through this tab.
 */
function watchForStaleState(): void {
  const panel = $<HTMLElement>('[data-premium-state]');
  if (!panel) return;

  const rendered = panel.dataset.premiumState ?? '';
  const guildId = panel.dataset.guildId ?? '';

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;

    void (async () => {
      try {
        const { premium } = await api.get<{ premium: PremiumState }>(
          `/api/billing/status?guildId=${guildId}`,
        );

        const now = [
          premium.active,
          premium.status,
          premium.cancelAtPeriodEnd,
          premium.renewsAt ?? '',
        ].join('|');

        // Reloaded only on a real change: a reload on every tab switch would
        // lose whatever the visitor had half-filled elsewhere on the page.
        if (now !== rendered) window.location.reload();
      } catch {
        // A failed read is not worth a message: the page is still showing what
        // it showed a second ago, which was true then.
      }
    })();
  });
}

interface PremiumState {
  active: boolean;
  status: string | null;
  cancelAtPeriodEnd: boolean;
  renewsAt: string | null;
}

/**
 * The fourteen days, shown only while they are open.
 *
 * Asked of the server rather than computed here: the window runs from the
 * payment, which the browser has never seen, and a date guessed from the
 * subscription's start would be wrong on every renewal.
 */
async function offerWithdrawal(): Promise<void> {
  const block = $<HTMLElement>('[data-withdrawal]');
  const button = $<HTMLButtonElement>('[data-withdrawal-button]');
  const text = $<HTMLElement>('[data-withdrawal-text]');
  if (!block || !button || !text) return;

  const guildId = button.dataset.guildId ?? '';

  let status: { eligible: boolean; reason?: string; deadline?: string };
  try {
    const result = await api.get<{ withdrawal: typeof status }>(
      `/api/billing/withdrawal?guildId=${guildId}`,
    );
    status = result.withdrawal;
  } catch {
    // Silent: a customer whose right has run out, or who never bought here,
    // must not be shown a red error about a button they were never offered.
    return;
  }

  /*
   * Nothing bought here — a granted premium, or one bought in Discord's shop.
   * Silence is right: there is no refund to explain the absence of.
   */
  if (status.reason === 'notBought') return;

  const until = status.deadline ? new Date(status.deadline).toLocaleDateString() : '';

  block.classList.remove('hidden');
  block.classList.add('flex');

  if (status.eligible) {
    text.textContent = ct('withdrawalOffer', 'You may still be refunded in full, until {date}.', {
      date: until,
    });
  } else {
    /*
     * Said, not hidden.
     *
     * The block used to disappear whenever a refund was impossible, so a
     * customer who had just subscribed and came looking for the fourteen days
     * found nothing at all — no button, no sentence, no way to tell an absent
     * feature from a broken one. The commonest case is the one that reads worst:
     * a subscription whose first invoice came to nothing, where there is
     * genuinely no money to give back.
     */
    button.remove();
    text.textContent =
      {
        noPayment: ct(
          'withdrawalNoPayment',
          'Nothing has been charged yet, so there is nothing to refund. Cancel the renewal from the portal above.',
        ),
        alreadyRefunded: ct('withdrawalAlready', 'This payment has already been refunded.'),
        tooLate: ct('withdrawalTooLate', 'The fourteen-day window closed on {date}.', {
          date: until,
        }),
        // An unknown reason falls back to the commonest one rather than to
        // nothing: an empty sentence is the silence this block exists to end.
      }[status.reason ?? 'noPayment'] ??
      ct('withdrawalNoPayment', 'Nothing has been charged yet, so there is nothing to refund.');

    return;
  }

  button.addEventListener('click', () => {
    /*
     * Confirmed, and deliberately by the browser's own dialogue.
     *
     * The money goes back and the access goes away at once. A custom modal
     * would be prettier and would also be one more thing between a customer and
     * a promise this page has just made them.
     */
    if (!window.confirm(ct('withdrawalConfirm', 'Refund in full and end premium access now?'))) {
      return;
    }

    void (async () => {
      setLoading(button, true);
      try {
        await api.post(`/api/billing/withdrawal?guildId=${guildId}`, { guildId });
        window.location.reload();
      } catch (error) {
        toastError(error);
        setLoading(button, false);
      }
    })();
  });
}

/**
 * Watches for activation after a successful payment.
 *
 * The browser's return proves nothing: only the webhook activates the
 * subscription. So we poll the status until activation, then reload. Past the
 * deadline, we replace the reassuring message with the likely cause — the event
 * never reached the server — rather than leaving the user to guess.
 */
function watchActivation(): void {
  const banner = $<HTMLElement>('[data-checkout-poll]');
  if (!banner) return;

  const guildId = banner.dataset.guildId ?? '';
  const message = $('[data-checkout-message]', banner);
  const spinner = $('.loading', banner);

  const ATTEMPTS = 10;
  const INTERVAL_MS = 2_000;
  let attempt = 0;

  if (message && banner.dataset.pending) message.textContent = banner.dataset.pending;

  const stop = (text: string, failed: boolean) => {
    spinner?.remove();
    if (message) message.textContent = text;
    if (failed) banner.classList.replace('alert-success', 'alert-warning');
  };

  const poll = async (): Promise<void> => {
    attempt += 1;

    try {
      const result = await api.get<{ premium: { active: boolean } }>(
        `/api/billing/status?guildId=${guildId}`,
      );
      if (result.premium.active) {
        window.location.reload();
        return;
      }
    } catch {
      // A transient network error: we retry until the attempts run out.
    }

    if (attempt >= ATTEMPTS) {
      stop(banner.dataset.timeout ?? '', true);
      return;
    }

    setTimeout(() => void poll(), INTERVAL_MS);
  };

  setTimeout(() => void poll(), INTERVAL_MS);
}
